import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Paper from '@mui/material/Paper';
import { DataGrid, Badge, FilterSelect, Spinner } from '@repo/components';
import { Event, Group, Task, User } from '@repo/shared/domain';
import { sortToString, stringToSort, constructQuery } from '@repo/shared/helpers';
import useUpdateQueryParams from 'src/hooks/useUpdateQueryParams';
import { getApprovals } from 'src/api/admin/sites';

type TargetedApproval = Task & {
	approvalTarget?: (User & { type: 'user' }) | (Group & { type: 'group' }) | (Event & { type: 'event' });
};

interface ApprovalsSearch {
	sort?: string;
	page?: number;
	search?: string;
	status?: 'all' | 'todo' | 'locked';
	targetType?: 'all' | 'user' | 'group' | 'event';
}

export const Route = createFileRoute('/_admin/$siteId/overview/approvals')({
	validateSearch: (search: Record<string, unknown>): ApprovalsSearch => ({
		sort: (search.sort as string) || 'createdAt:desc',
		page: search.page as number | undefined,
		search: search.search as string | undefined,
		status: search.status as 'all' | 'todo' | 'locked' | undefined,
		targetType: search.targetType as 'all' | 'user' | 'group' | 'event' | undefined,
	}),
	component: ApprovalsPage,
});

function ApprovalsPage() {
	const { sort = 'createdAt:desc', page, search, status, targetType } = Route.useSearch();
	const updateQueryParams = useUpdateQueryParams();
	const queryClient = useQueryClient();

	const { data: approvalsResponse, isLoading } = useQuery({
		queryKey: ['approvals', sort, page, search, status, targetType],
		queryFn: () => getApprovals(true, constructQuery({ sort, page, search, status, targetType })),
	});

	if (isLoading || !approvalsResponse) {
		return (
			<div className="h-full w-full flex items-center justify-center p-8">
				<Spinner />
			</div>
		);
	}

	const renderEntityBadge = (targetedApproval: TargetedApproval) => {
		const approvalTarget = targetedApproval.approvalTarget;

		if (!approvalTarget) {
			return null;
		}

		if (approvalTarget.type === 'user') {
			return <Badge>{[approvalTarget.firstName, approvalTarget.lastName].join(' ')}</Badge>;
		}
		if (approvalTarget.type === 'group') {
			return <Badge>{approvalTarget.name}</Badge>;
		}
		if (approvalTarget.type === 'event') {
			return <Badge>{approvalTarget.name}</Badge>;
		}
	};

	const columns = [
		{ field: 'name', headerName: 'Description', flex: 1, sortable: false },
		{
			field: 'status',
			headerName: 'Approval For',
			flex: 1,
			sortable: false,
			renderCell: (c: { row: TargetedApproval }) => renderEntityBadge(c.row),
		},
		{
			field: 'createdAt',
			headerName: 'Assigned on',
			flex: 1,
			renderCell: (c: { row: TargetedApproval }) => new Date(c.row.createdAt).toLocaleDateString('en-GB'),
		},
	];

	const handleTargetedApprovalRowClick = ({ row }: { row: Task }) => {
		if (row.userId) {
			window.open(`/users/${row.userId}/approvals`, '_blank');
			return;
		}
		if (row.groupId) {
			window.open(`/groups/${row.groupId}/approvals`, '_blank');
			return;
		}
		if (row.eventId) {
			window.open(`/events/${row.eventId}/approvals`, '_blank');
			return;
		}
	};

	const targetedApprovals: TargetedApproval[] = approvalsResponse.data.map((approval) => {
		if (approval.userId && approval.user) {
			return {
				...approval,
				approvalTarget: Object.assign(approval.user, {
					type: 'user' as const,
				}),
			};
		}
		if (approval.groupId && approval.group) {
			return {
				...approval,
				approvalTarget: Object.assign(approval.group, {
					type: 'group' as const,
				}),
			};
		}
		if (approval.eventId && approval.event) {
			return {
				...approval,
				approvalTarget: Object.assign(approval.event, {
					type: 'event' as const,
				}),
			};
		}

		return approval;
	});

	const handlePaginationModelChange = ({ page }: { page: number }) => {
		updateQueryParams({ page });
	};

	const handleStatusFilterChange = (value: string) => {
		updateQueryParams({ status: value });
	};

	const handleTargetTypeFilterChange = (value: string) => {
		updateQueryParams({ targetType: value, page: undefined });
	};

	const handleSort = ([sortModel]: [{ field: string; sort: 'asc' | 'desc' } | undefined]) => {
		if (!sortModel) {
			return;
		}

		updateQueryParams({ sort: sortToString(sortModel) });
	};

	const statusOptions = [
		{ value: 'all', label: 'All Open Approvals' },
		{ value: 'todo', label: 'Ready for Approval' },
		{ value: 'locked', label: 'Not yet ready for Approval' },
	];

	const targetTypeOptions = [
		{ value: 'all', label: 'All Types' },
		{ value: 'group', label: 'Company Approvals' },
		{ value: 'event', label: 'Event Approvals' },
		{ value: 'user', label: 'User Approvals' },
	];

	return (
		<div className="bg-white p-4">
			<div className="flex gap-3 mb-2">
				<FilterSelect
					options={statusOptions}
					listSubHeader="Filter Approvals"
					value={status || 'todo'}
					onChange={handleStatusFilterChange}
					sx={{ width: 280 }}
				/>
				<FilterSelect
					options={targetTypeOptions}
					listSubHeader="Filter by Type"
					value={targetType || 'all'}
					onChange={handleTargetTypeFilterChange}
					sx={{ width: 220 }}
				/>
			</div>
			<Paper variant="outlined">
				<DataGrid
					data={targetedApprovals}
					columns={columns}
					rowCount={approvalsResponse.count}
					paginationMode="server"
					sortingMode="server"
					onPaginationModelChange={handlePaginationModelChange}
					pageSize={approvalsResponse.pageSize}
					pageSizeOptions={[approvalsResponse.pageSize]}
					page={approvalsResponse.page}
					onSortModelChange={handleSort}
					sortingOrder={['asc', 'desc']}
					sortModel={[stringToSort(sort)]}
					onReload={() => queryClient.invalidateQueries({ queryKey: ['approvals'] })}
					onRowClick={handleTargetedApprovalRowClick}
				/>
			</Paper>
		</div>
	);
}
